import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Drag-and-drop multi-file uploader.
// Reads files, validates against maxFiles/maxSize, renders a row per file by
// cloning fileTemplate into fileList, then POSTs each file individually to
// uploadUrl (document[file] + document[category]) with per-file progress.
export default class extends Controller {
  static targets = [
    "dropzone",
    "input",
    "fileList",
    "fileTemplate",
    "submit",
    "categorySelect"
  ]

  static values = {
    uploadUrl: String,
    refreshMode: { type: String, default: "page" },
    maxFiles: { type: Number, default: 20 },
    maxSize: { type: Number, default: 25 }, // megabytes
    csrfToken: String,
    // When set, upload straight to the storage service and post only the
    // resulting signed_id. Bytes never pass through Rails, which is what makes
    // a 50-100 photo batch safe on a small web machine.
    directUploadUrl: String,
    // Shared semantic colors keep drag, progress, and error states consistent.
    // Screens can override these values for a specific upload surface.
    uploadingTextClass: { type: String, default: "text-clay-700" },
    doneTextClass: { type: String, default: "text-moss-600" },
    errorTextClass: { type: String, default: "text-danger-600" },
    idleClass: { type: String, default: "border-clay-300" },
    activeClass: { type: String, default: "border-clay-600 bg-clay-50" },
    errorClass: { type: String, default: "border-danger-400" }
  }

  // Space-separated class strings -> arrays, for classList add/remove.
  // Absolute form of uploadUrlValue, for comparing against xhr.responseURL.
  get absoluteUploadUrl() {
    return new URL(this.uploadUrlValue, window.location.origin).href
  }

  get idleClasses() { return this.idleClassValue.split(" ").filter(Boolean) }
  get activeClasses() { return this.activeClassValue.split(" ").filter(Boolean) }
  get errorClasses() { return this.errorClassValue.split(" ").filter(Boolean) }

  connect() {
    // Each entry: { id, file, row, status }
    this.files = []
    this.nextId = 0
    this.uploading = false
    this.cancelled = false
    this.activeRequests = new Set()
    this.pendingResolves = new Set()
    this.leavePage = () => this.cancelUploads()
    document.addEventListener("turbo:before-render", this.leavePage)
    window.addEventListener("pagehide", this.leavePage)
    // A Turbo snapshot cannot retain File objects or active request state.
    if (this.hasFileListTarget) this.fileListTarget.replaceChildren()
    this.updateSubmitState()
  }

  // --- File input ----------------------------------------------------------

  triggerFileInput(event) {
    // Ignore clicks that originate from the hidden input itself bubbling back
    // up through the dropzone to avoid re-opening the file dialog recursively.
    if (event && event.target === this.inputTarget) return
    if (this.hasInputTarget) this.inputTarget.click()
  }

  // Enter/Space open the file picker, so the dropzone behaves like the button
  // its role claims. Without this the picker is mouse-only.
  handleDropzoneKeydown(event) {
    if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return
    event.preventDefault()
    this.triggerFileInput(event)
  }

  handleFileSelected() {
    if (!this.hasInputTarget) return
    this.addFiles(this.inputTarget.files)
    // Reset so selecting the same file again re-fires `change`.
    this.inputTarget.value = ""
  }

  // --- Drag & drop ---------------------------------------------------------

  handleDragEnter(event) {
    event.preventDefault()
    this.activateDropzone()
  }

  handleDragOver(event) {
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
    this.activateDropzone()
  }

  handleDragLeave(event) {
    event.preventDefault()
    // Only deactivate when leaving the dropzone entirely, not when moving
    // between its children.
    if (
      this.hasDropzoneTarget &&
      event.relatedTarget &&
      this.dropzoneTarget.contains(event.relatedTarget)
    ) {
      return
    }
    this.deactivateDropzone()
  }

  handleDrop(event) {
    event.preventDefault()
    this.deactivateDropzone()
    if (event.dataTransfer) this.addFiles(event.dataTransfer.files)
  }

  activateDropzone() {
    if (!this.hasDropzoneTarget) return
    this.dropzoneTarget.classList.add(...this.activeClasses)
    this.dropzoneTarget.classList.remove(...this.idleClasses)
  }

  deactivateDropzone() {
    if (!this.hasDropzoneTarget) return
    this.dropzoneTarget.classList.remove(...this.activeClasses)
    this.dropzoneTarget.classList.add(...this.idleClasses)
  }

  // --- File list management ------------------------------------------------

  addFiles(fileList) {
    const incoming = Array.from(fileList || [])
    if (incoming.length === 0) return

    for (const file of incoming) {
      if (this.files.length >= this.maxFilesValue) {
        this.flashDropzoneError(`Maximum of ${this.maxFilesValue} files allowed.`)
        break
      }
      const entry = this.buildEntry(file)
      this.files.push(entry)
      this.fileListTarget.appendChild(entry.row)
    }

    this.updateSubmitState()
  }

  buildEntry(file) {
    const id = this.nextId++
    const fragment = this.fileTemplateTarget.content.cloneNode(true)
    const row = fragment.querySelector("[data-file-item]")

    const nameEl = row.querySelector("[data-file-name]")
    const sizeEl = row.querySelector("[data-file-size]")
    const previewEl = row.querySelector("[data-file-preview]")
    const removeBtn = row.querySelector("[data-remove-file]")

    if (nameEl) nameEl.textContent = file.name
    if (sizeEl) sizeEl.textContent = this.formatSize(file.size)

    const entry = { id, file, row, status: "pending" }

    // Image preview thumbnail
    if (previewEl && file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file)
      previewEl.src = url
      previewEl.classList.remove("hidden")
      previewEl.dataset.objectUrl = url
    }

    // Per-file validation against maxSize (MB -> bytes)
    const maxBytes = this.maxSizeValue * 1024 * 1024
    if (file.size > maxBytes) {
      entry.status = "invalid"
      this.setStatus(entry, `Exceeds ${this.maxSizeValue}MB`, this.errorTextClassValue)
    }

    if (removeBtn) {
      removeBtn.addEventListener("click", () => this.removeFile(id))
    }

    return entry
  }

  removeFile(id) {
    const index = this.files.findIndex((e) => e.id === id)
    if (index === -1) return

    const [entry] = this.files.splice(index, 1)
    const url = entry.row.querySelector("[data-file-preview]")?.dataset.objectUrl
    if (url) URL.revokeObjectURL(url)
    entry.row.remove()

    this.updateSubmitState()
  }

  // --- Upload --------------------------------------------------------------

  async uploadAll() {
    const category = this.hasCategorySelectTarget
      ? this.categorySelectTarget.value
      : ""

    if (!category) {
      this.flashDropzoneError("Please select a document type first.")
      if (this.hasCategorySelectTarget) this.categorySelectTarget.focus()
      return
    }

    const pending = this.files.filter(
      (e) => e.status === "pending" || e.status === "error"
    )
    if (pending.length === 0) return

    this.uploading = true
    this.cancelled = false
    if (this.hasSubmitTarget) this.submitTarget.disabled = true

    for (const entry of pending) {
      if (this.cancelled) break
      await this.uploadOne(entry, category)
    }
    if (this.cancelled) return

    this.uploading = false
    this.updateSubmitState()

    const savedCount = pending.filter((entry) => entry.status === "done").length
    const failedCount = this.files.filter((entry) => entry.status === "error" || entry.status === "invalid").length
    if (savedCount && this.refreshModeValue === "explorer") {
      this.dispatch("complete", { detail: { savedCount, failedCount } })
    } else if (savedCount) {
      window.Turbo
        ? window.Turbo.visit(window.location.href)
        : window.location.reload()
    }
  }

  uploadOne(entry, category) {
    if (this.hasDirectUploadUrlValue && this.directUploadUrlValue) {
      return this.uploadDirect(entry, category)
    }
    return this.uploadThroughRails(entry, category)
  }

  // Browser -> storage service, then POST the signed_id. Progress comes from
  // the PUT itself, so the bar still tracks the real transfer.
  uploadDirect(entry, category) {
    return new Promise((resolve) => {
      entry.status = "uploading"
      this.setStatus(entry, "Uploading…", this.uploadingTextClassValue)
      this.showProgress(entry)
      this.setProgress(entry, 0)

      this.pendingResolves.add(resolve)
      const upload = new DirectUpload(entry.file, this.directUploadUrlValue, {
        directUploadWillCreateBlobWithXHR: (xhr) => this.trackRequest(xhr),
        directUploadWillStoreFileWithXHR: (xhr) => {
          this.trackRequest(xhr)
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              this.setProgress(entry, Math.round((event.loaded / event.total) * 100))
            }
          })
        }
      })

      upload.create((error, blob) => {
        this.pendingResolves.delete(resolve)
        if (this.cancelled) { resolve(); return }
        if (error) {
          entry.status = "error"
          this.hideProgress(entry)
          // A failure here is usually the sign-in requirement on the
          // direct-upload endpoint, or the service rejecting the PUT.
          this.setStatus(entry, "Upload failed", this.errorTextClassValue)
          resolve()
          return
        }

        // Attach by signed_id. The bytes are already in the service, and
        // Document#acceptable_file re-reads them there to enforce the
        // magic-byte allowlist and the size cap.
        this.postSignedId(entry, category, blob.signed_id).then(resolve)
      })
    })
  }

  postSignedId(entry, category, signedId) {
    return new Promise((resolve) => {
      const formData = new FormData()
      formData.append("document[file]", signedId)
      formData.append("document[category]", category)
      this.sendFormData(entry, formData, resolve)
    })
  }

  uploadThroughRails(entry, category) {
    return new Promise((resolve) => {
      entry.status = "uploading"
      this.setStatus(entry, "Uploading…", this.uploadingTextClassValue)
      this.showProgress(entry)
      this.setProgress(entry, 0)

      const formData = new FormData()
      formData.append("document[file]", entry.file)
      formData.append("document[category]", category)

      this.sendFormData(entry, formData, resolve, { trackProgress: true })
    })
  }

  // Shared POST to the documents endpoint, used by both upload paths.
  sendFormData(entry, formData, resolve, { trackProgress = false } = {}) {
    const xhr = new XMLHttpRequest()
    this.trackRequest(xhr)
    xhr.addEventListener("abort", resolve, { once: true })
    xhr.open("POST", this.uploadUrlValue, true)
    xhr.setRequestHeader("Accept", "application/json")
    xhr.setRequestHeader("X-CSRF-Token", this.csrfToken)
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest")

    if (trackProgress) {
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          this.setProgress(entry, percent)
        }
      })
    }

    xhr.addEventListener("load", () => {
      if (this.cancelled) { resolve(); return }
      // A 302 (lost session, revoked role) is followed transparently by XHR
      // and surfaces as 200 with an HTML body. Without this check the file
      // would be reported "Uploaded" when nothing was saved. The server also
      // answers 403 JSON now; this guards the sign-in redirect as well.
      const redirected =
        xhr.responseURL && xhr.responseURL.split("?")[0] !== this.absoluteUploadUrl
      if (redirected) {
        entry.status = "error"
        this.hideProgress(entry)
        this.setStatus(entry, "Session expired — sign in again", this.errorTextClassValue)
        resolve()
        return
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        entry.status = "done"
        this.setProgress(entry, 100)
        this.hideProgress(entry)
        this.setStatus(entry, "Uploaded", this.doneTextClassValue)
        this.disableRemove(entry)
      } else {
        entry.status = "error"
        this.hideProgress(entry)
        this.setStatus(entry, this.parseError(xhr) || "Failed", this.errorTextClassValue)
      }
      resolve()
    })

    xhr.addEventListener("error", () => {
      entry.status = "error"
      this.hideProgress(entry)
      this.setStatus(entry, "Network error", this.errorTextClassValue)
      resolve()
    })

    xhr.send(formData)
  }

  parseError(xhr) {
    try {
      const data = JSON.parse(xhr.responseText)
      if (data && Array.isArray(data.errors)) return data.errors.join(", ")
      if (data && data.error) return data.error
    } catch (_e) {
      // non-JSON response, fall through
    }
    return null
  }

  // --- Helpers -------------------------------------------------------------

  get csrfToken() {
    if (this.hasCsrfTokenValue && this.csrfTokenValue) return this.csrfTokenValue
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  setStatus(entry, text, className) {
    const el = entry.row.querySelector("[data-status-text]")
    if (!el) return
    el.textContent = text
    el.className = `text-xs ${className || ""}`.trim()
  }

  showProgress(entry) {
    entry.row
      .querySelector("[data-progress-container]")
      ?.classList.remove("hidden")
  }

  hideProgress(entry) {
    entry.row
      .querySelector("[data-progress-container]")
      ?.classList.add("hidden")
  }

  setProgress(entry, percent) {
    const bar = entry.row.querySelector("[data-progress-bar]")
    if (bar) bar.style.width = `${percent}%`
  }

  disableRemove(entry) {
    const removeBtn = entry.row.querySelector("[data-remove-file]")
    if (removeBtn) {
      removeBtn.disabled = true
      removeBtn.classList.add("hidden")
    }
  }

  flashDropzoneError(message) {
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add(...this.errorClasses)
      this.dropzoneTarget.classList.remove(...this.idleClasses)
      window.clearTimeout(this._flashTimeout)
      this._flashTimeout = window.setTimeout(() => {
        this.dropzoneTarget.classList.remove(...this.errorClasses)
        this.dropzoneTarget.classList.add(...this.idleClasses)
      }, 2000)
    }
    window.alert(message)
  }

  updateSubmitState() {
    if (!this.hasSubmitTarget) return
    const uploadable = this.files.some(
      (e) => e.status === "pending" || e.status === "error"
    )
    this.submitTarget.disabled = this.uploading === true || !uploadable
    const retryOnly = !this.files.some((entry) => entry.status === "pending") && this.files.some((entry) => entry.status === "error")
    this.submitTarget.textContent = this.uploading ? "Uploading…" : (retryOnly ? "Retry failed files" : "Upload")
  }

  formatSize(bytes) {
    if (bytes == null) return ""
    if (bytes < 1024) return `${bytes} B`
    const kb = bytes / 1024
    if (kb < 1024) return `${kb.toFixed(1)} KB`
    return `${(kb / 1024).toFixed(1)} MB`
  }

  trackRequest(xhr) {
    this.activeRequests.add(xhr)
    xhr.addEventListener("loadend", () => this.activeRequests.delete(xhr), { once: true })
  }

  cancelUploads() {
    // The browser can stop pending work; a request already accepted by the
    // server may still finish. No document rollback is implied here.
    this.cancelled = true
    for (const xhr of this.activeRequests) xhr.abort()
    this.activeRequests.clear()
    for (const resolve of this.pendingResolves) resolve()
    this.pendingResolves.clear()
  }

  disconnect() {
    this.cancelUploads()
    document.removeEventListener("turbo:before-render", this.leavePage)
    window.removeEventListener("pagehide", this.leavePage)
    // Release any object URLs created for previews.
    for (const entry of this.files) {
      const url = entry.row.querySelector("[data-file-preview]")?.dataset
        .objectUrl
      if (url) URL.revokeObjectURL(url)
    }
    window.clearTimeout(this._flashTimeout)
  }
}
